import React, {
  useRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useState,
} from "react";
import withDefaults from "../../../utils/with-defaults";
import { NormalTypes } from "../../../utils/prop-types";
import { getColors } from "../Input/styles";

interface Props {
  name?: string;
  value?: string;
  label?: string;
  initialValue?: string;
  placeholder?: string;
  status?: NormalTypes;
  width?: string;
  minHeight?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  className?: string;
}

const defaultProps = {
  initialValue: "",
  status: "default" as NormalTypes,
  width: "initial",
  minHeight: "6.25rem",
  disabled: false,
  readOnly: false,
  className: "",
};

type NativeAttrs = Omit<React.TextareaHTMLAttributes<any>, keyof Props>;
export type TextareaProps = Props & typeof defaultProps & NativeAttrs;

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.PropsWithChildren<TextareaProps>
>(
  (
    {
      name,
      width,
      status,
      minHeight,
      disabled,
      readOnly,
      onFocus,
      onBlur,
      className,
      initialValue,
      onChange,
      value,
      placeholder,
      label,
      ...props
    },
    ref: React.Ref<HTMLTextAreaElement | null>
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => textareaRef.current);
    const isControlledComponent = useMemo(() => value !== undefined, [value]);
    const [selfValue, setSelfValue] = useState<string>(initialValue);
    const [hover, setHover] = useState<boolean>(false);
    const colors = useMemo(() => getColors(status), [status]);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (disabled || readOnly) return;
      setSelfValue(event.target.value);
      onChange && onChange(event);
    };
    const focusHandler = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setHover(true);
      onFocus && onFocus(e);
    };
    const blurHandler = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setHover(false);
      onBlur && onBlur(e);
    };

    useEffect(() => {
      if (isControlledComponent) {
        setSelfValue(value as string);
      }
    });

    const controlledValue = isControlledComponent
      ? { value: selfValue }
      : { defaultValue: initialValue };
    const textareaProps = {
      ...props,
      ...controlledValue,
    };

    return (
      <div>
        <div
          className={`text-sm text-gray-700 mb-2 ${!label ? "hidden" : null}`}
        >
          <label
            className="text-sm font-medium"
            id={[name, "label"].join("-")}
            htmlFor={[name, "textarea"].join("-")}
          >
            {label}
          </label>
        </div>
        <div
          className={`wrapper inline-flex box-border select-none min-w-4xs h-auto border rounded-md  ${
            disabled
              ? "disabled bg-gray-100 border-gray-200 text-gray-700 cursor-not-allowed"
              : colors
          } ${className}`}
        >
          <textarea
            className={`text-sm bg-transparent shadow-none block w-full h-full resize-none border-none outline-none p-2  focus: focus:ring-0`}
            name={name}
            ref={textareaRef}
            disabled={disabled}
            placeholder={placeholder}
            readOnly={readOnly}
            onFocus={focusHandler}
            onBlur={blurHandler}
            onChange={changeHandler}
            {...textareaProps}
          />
        </div>
        <style jsx>{`
          .wrapper {
            width: ${width};
            max-width: 95vw;
            transition: border 0.2s ease 0s, color 0.2s ease 0s;
          }

          textarea {
            min-height: ${minHeight};
          }

          .disabled > textarea {
            cursor: not-allowed;
          }

          textarea:-webkit-autofill,
          textarea:-webkit-autofill:hover,
          textarea:-webkit-autofill:active,
          textarea:-webkit-autofill:focus {
            -webkit-box-shadow: 0 0 0 30px #fff inset !important;
          }
        `}</style>
      </div>
    );
  }
);

export default withDefaults(Textarea, defaultProps);
